import { Locale } from 'App/i18n/enums';
import { MoneyObjectInterface } from 'commons/types';
import { Currency } from 'commons/types/Currency.enum';
import { config } from 'config';
import { IStoreInfo } from 'redux/slices/types';
import 'moment/locale/es';
import 'moment/locale/pt';

export const getCurrencyByCountry = (country: string) => {
  switch (country) {
    case 'AR':
      return 'ARS';

    case 'BR':
      return 'BRL';

    case 'MX':
      return 'MXN';

    default:
      break;
  }
};

export const formatCurrency = (
  money: MoneyObjectInterface,
  storeInfo: IStoreInfo,
): string => {
  const locale = `${storeInfo.language}-${storeInfo.country}`;
  const currency = money.currency || getCurrencyByCountry(storeInfo.country);

  if (currency === Currency.USD || currency === Currency.MXN) {
    const numberFormatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(money.value);

    return `${currency} ${numberFormatted}`;
  }

  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(money.value);

  return formatted.replace(/\s/g, '');
};

export const formatCurrencyWithStoreInfo = (
  value: number,
  storeInfo: IStoreInfo,
): string => {
  const locale = `${storeInfo.language}-${storeInfo.country}`;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: getCurrencyByCountry(storeInfo.country),
  })
    .format(value)
    .replace(/\s/g, '');
};

export const getFixedLocaleFromLocale = (locale: string): Locale => {
  const language = locale.split('-')[0];
  if (language === 'es') {
    if (locale === Locale.ARGENTINA) {
      return Locale.ARGENTINA;
    } else {
      return Locale.MEXICO;
    }
  } else {
    return Locale.BRAZIL;
  }
};

export const isJuridicalPerson = (fiscalNumber?: string): boolean => {
  if (!fiscalNumber) return false;
  return fiscalNumber.startsWith('3');
};

// Amplitude `legal_entity_type` property.
// Returns undefined when the fiscal number is unknown.
export const getLegalEntityType = (
  fiscalNumber?: string,
): 'juridical_person' | 'fisical_person' | undefined => {
  if (!fiscalNumber) return undefined;
  return isJuridicalPerson(fiscalNumber)
    ? 'juridical_person'
    : 'fisical_person';
};

export const getCuitOrCuilLabel = (fiscalNumber: string): string => {
  if (!fiscalNumber) return 'CUIT/CUIL';
  if (isJuridicalPerson(fiscalNumber)) {
    return 'CUIT';
  }
  return 'CUIT/CUIL';
};

export const formatCuit = (cuit: string): string => {
  if (!cuit) return '';
  const lastInx = cuit.length - 1;
  return `${cuit.substring(0, 2)}-${cuit.substring(
    2,
    lastInx,
  )}-${cuit.substring(lastInx)}`;
};

export const pdfThumbnailPath = `${config.publicUrl}/assets/img/pdfThumbnail.png`;

export const capitalize = (word: string): string => {
  return word[0].toUpperCase() + word.slice(1).toLowerCase();
};

export const formatPercentage = (value: number): string => {
  const normalizedValue = value <= 1 ? value * 100 : value;
  return (
    // eslint-disable-next-line prefer-template
    Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(
      normalizedValue,
    ) + '%'
  );
};

export const isNotEmpty = (obj: object | null | undefined): boolean =>
  obj != null && Object.keys(obj).length > 0;

export const snakeToTitleCase = (s: string) => {
  return s
    .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
    .replace(/[-_]+(.)/g, (_, c) => ` ${c.toUpperCase()}`);
};

export { default as transactionLabelProps } from './transactionStatusLabelProps';
export { default as transactionTagProps } from './transactionTagProps';
export { default as withdrawTagProps } from './withdrawStatusTagProps';
export { default as useTranslationWithPrefix } from './useTranslationWithPrefix';
export { default as redirectToOrder } from './redirectToOrder';
export { default as bankAccountIsUnknown } from './bankAccountIsUnknown';
export * from './dateUtils';
export * from './failure-code.enum';
export * from './transformToCamel';
export * from './getUrlSearchParams';
export * as cbuValidator from './cbu';
export * from './downloadFromUrl';
export * from './redirectToTransaction';
export * from './isOldTransaction';
export * from './documentUploader';
export * from './uploadFileToS3';
export * from './validateFile';
export * from './formatCurrency';
export * from './constants';
